/**
 * Field Validation Utility
 * Provides comprehensive validation rule execution for form fields
 */

/**
 * Validate a field value against validation rules
 * @param {*} value - The field value to validate
 * @param {Object} validationRules - Validation rules configuration
 * @param {Array} validationRules.rules - Array of validation rule objects
 * @param {boolean} validationRules.allowEmpty - Allow empty values to bypass validation
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateField = (value, validationRules) => {
  if (!validationRules || !validationRules.rules || validationRules.rules.length === 0) {
    return { isValid: true, errors: [] };
  }

  // Handle empty values
  const isEmpty = value === null || value === undefined || value === '';
  if (isEmpty && validationRules.allowEmpty) {
    return { isValid: true, errors: [] };
  }

  const errors = [];
  const stringValue = String(value || '');

  for (const rule of validationRules.rules) {
    let isRuleValid = true;
    let errorMessage = rule.errorMessage || 'Validation failed';

    switch (rule.type) {
      case 'startsWith':
        isRuleValid = stringValue.startsWith(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must start with "${rule.value}"`;
        }
        break;

      case 'endsWith':
        isRuleValid = stringValue.endsWith(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must end with "${rule.value}"`;
        }
        break;

      case 'contains':
        isRuleValid = stringValue.includes(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must contain "${rule.value}"`;
        }
        break;

      case 'notContains':
        isRuleValid = !stringValue.includes(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must not contain "${rule.value}"`;
        }
        break;

      case 'regex':
        try {
          const regex = new RegExp(rule.pattern, rule.flags || '');
          isRuleValid = regex.test(stringValue);
          if (!errorMessage || errorMessage === 'Validation failed') {
            errorMessage = 'Invalid format';
          }
        } catch (e) {
          console.error('Invalid regex pattern:', rule.pattern, e);
          isRuleValid = false;
          errorMessage = 'Invalid validation pattern configured';
        }
        break;

      case 'isNumeric':
        isRuleValid = /^[0-9]+$/.test(stringValue);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = 'Must contain only numbers';
        }
        break;

      case 'isAlphanumeric':
        isRuleValid = /^[a-zA-Z0-9]+$/.test(stringValue);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = 'Must contain only letters and numbers';
        }
        break;

      case 'minLength':
        isRuleValid = stringValue.length >= Number(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must be at least ${rule.value} characters`;
        }
        break;

      case 'maxLength':
        isRuleValid = stringValue.length <= Number(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must not exceed ${rule.value} characters`;
        }
        break;

      case 'exactLength':
        isRuleValid = stringValue.length === Number(rule.value);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = `Must be exactly ${rule.value} characters`;
        }
        break;

      case 'oneOf':
        isRuleValid = Array.isArray(rule.values) && rule.values.includes(stringValue);
        if (!errorMessage || errorMessage === 'Validation failed') {
          errorMessage = 'Invalid value';
        }
        break;

      default:
        console.warn('Unknown validation rule type:', rule.type);
        continue;
    }

    if (!isRuleValid) {
      errors.push(errorMessage);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Common validation rule presets
 */
export const VALIDATION_PRESETS = {
  URL: {
    type: 'regex',
    pattern: '^https?:\\/\\/.+',
    errorMessage: 'Must be a valid URL'
  },
  HTTPS_URL: {
    type: 'startsWith',
    value: 'https://',
    errorMessage: 'Must start with https://'
  },
  NUMERIC: {
    type: 'isNumeric',
    errorMessage: 'Must contain only numbers'
  },
  ALPHANUMERIC: {
    type: 'isAlphanumeric',
    errorMessage: 'Must contain only letters and numbers'
  },
  EAN13: {
    type: 'regex',
    pattern: '^[0-9]{13}$',
    errorMessage: 'Must be a valid 13-digit EAN code'
  },
  EAN8: {
    type: 'regex',
    pattern: '^[0-9]{8}$',
    errorMessage: 'Must be a valid 8-digit EAN code'
  },
  UPC: {
    type: 'regex',
    pattern: '^[0-9]{12}$',
    errorMessage: 'Must be a valid 12-digit UPC code'
  },
  ISBN: {
    type: 'regex',
    pattern: '^(97[89])?[0-9]{10}$',
    errorMessage: 'Must be a valid ISBN'
  }
};
